/**
 * Background Premium Controls Component
 *
 * Premium background control features including gradient, image, pattern and overlay controls.
 * This component can be replaced by the premium plugin via filter hooks.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import type { MediaObject } from '@redfox-thank-you/controls';
import {
	BaseControl,
	GradientPicker,
	MediaUploadControl,
	SelectControl,
} from '@redfox-thank-you/controls';
import { ToggleControl } from '@wordpress/components';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { patternOptions } from './constants';
import {
	BackgroundControlProps,
	BackgroundValues,
	ResponsiveBreakpoint,
} from './types';

interface BackgroundPremiumControlsProps extends BackgroundControlProps {
	activeBreakpoint: ResponsiveBreakpoint;
	controls: Record<string, boolean>;
	currentValues: BackgroundValues;
	updateAttribute: (
		key: keyof BackgroundValues
	) => (value: string | number | undefined) => void;
	resetIndividualProperty: (property: keyof BackgroundValues) => () => void;
hasPropertyValue: (property: keyof BackgroundValues) => boolean;
	showIndividualResets: boolean;
}

export const BackgroundPremiumControls: React.FC<
	BackgroundPremiumControlsProps
> = ({
	attributes,
	setAttributes,
	activeBreakpoint,
	controls,
	currentValues,
	updateAttribute,
	resetIndividualProperty,
	hasPropertyValue,
	showIndividualResets,
}) => {
	const premiumControls = (
		<>
			{ /* Background Gradient */}
			{controls.backgroundGradient && (
				<BaseControl
					id="background-gradient"
					label={__('Background Gradient', 'redfox-thank-you')}
					inline
					isPro
				>
					<GradientPicker
						value={
							currentValues.backgroundGradient ??
								currentValues.customGradient
						}
						onChange={() => { }}
					/>
				</BaseControl>
			)}

			{ /* Background Image */}
			{controls.backgroundImage && (
				<>
					<BaseControl
						id="background-image"
						label={__('Background Image', 'redfox-thank-you')}
						isPro
					>
						<MediaUploadControl
							value={
								currentValues.backgroundImage?.id &&
										currentValues.backgroundImage?.url
									? ({
										id: currentValues.backgroundImage
											.id,
										url: currentValues.backgroundImage
											.url,
										alt:
												currentValues.backgroundImage
													.alt ?? '',
										mime:
												currentValues.backgroundImage
													.mime ?? '',
									} as MediaObject)
									: undefined
							}
							onChange={() => { }}
							allowedTypes={['image']}
							uploadText={__(
								'Select Image',
								'redfox-thank-you',
							)}
							replaceText={__('Replace', 'redfox-thank-you')}
							removeText={__('Remove', 'redfox-thank-you')}
							showPreview={true}
						/>
					</BaseControl>
				</>
			)}

			{ /* Background Pattern */}
			{controls.backgroundPattern && (
				<>
					<BaseControl
						id="background-pattern"
						label={__('Background Pattern', 'redfox-thank-you')}
						isPro
					>
						<SelectControl
							label=""
							value={currentValues.backgroundPattern ?? ''}
							onChange={() => { }}
							options={patternOptions}
						/>
					</BaseControl>
				</>
			)}

			{ /* Background Overlay */}
			{controls.backgroundOverlay && (
				<>
					<BaseControl
						id="overlay-toggle"
						label={__('Background Overlay', 'redfox-thank-you')}
						inline
						isPro
					>
						<ToggleControl
							label=""
							checked={
								currentValues.backgroundOverlay?.enabled ??
									false
							}
							onChange={() => { }}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</>
			)}
		</>
	);

	/**
		 * Apply filter hook to allow premium plugin to replace the premium controls
		 *
		 * @param {React.ReactNode} premiumControls - The default premium controls
		 * @param {BackgroundPremiumControlsProps} props - All the component props
		 * @returns {React.ReactNode} - The filtered premium controls
		 */
	return applyFilters(
		'redfox_thank_you_background_premium_controls',
		premiumControls,
		{
			attributes,
			setAttributes,
			activeBreakpoint,
			controls,
			currentValues,
			updateAttribute,
			resetIndividualProperty,
			hasPropertyValue,
			showIndividualResets,
		},
	) as React.ReactElement;
};
